import { memo, useCallback, useRef, useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Eye, EyeOff, User, Copy, Hash, Coins } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { OpuPermissions } from "@/lib/permissions";
import { useIsInWatchlist, useToggleWatchlist } from "@/hooks/useWatchlist";
import type { AuctionItem } from "@/types/auction";

// ─── Global Coordinator ──────────────────────────────────────────────
// Only one context menu should be open at a time across all cards.
// When a new menu opens, the previous one is dismissed via Escape key.
let _dismissActive: (() => void) | null = null;

interface AuctionCardContextMenuProps {
  auction: AuctionItem;
  auctionId: string;
  rawAuctionKey: string;
  children: ReactNode;
}

const AuctionCardContextMenu = memo(({ auction, auctionId, rawAuctionKey, children }: AuctionCardContextMenuProps) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { has: hasPermission } = usePermissions();
  const canWatchlist = isAuthenticated && hasPermission(OpuPermissions.USE_WATCHLIST);
  const isInWatchlist = useIsInWatchlist(user?.id, rawAuctionKey);
  const { toggle, isPending } = useToggleWatchlist();
  const contentRef = useRef<HTMLDivElement>(null);

  const auctionUrl = `${window.location.origin}/auction/${auctionId}`;
  const priceText = auction.currentBid.toLocaleString("de-DE");

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  }, []);

  // Dismiss THIS menu by simulating Escape on the content element
  const dismissSelf = useCallback(() => {
    contentRef.current?.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true })
    );
  }, []);

  // Coordinate open/close across all AuctionCardContextMenu instances
  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      // Close any other open context menu first
      _dismissActive?.();
      // Register ourselves as the active menu
      _dismissActive = dismissSelf;
    } else {
      // We're closing – clear if we're still the active one
      if (_dismissActive === dismissSelf) {
        _dismissActive = null;
      }
    }
  }, [dismissSelf]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (_dismissActive === dismissSelf) {
        _dismissActive = null;
      }
    };
  }, [dismissSelf]);

  return (
    <ContextMenu onOpenChange={handleOpenChange}>
      <ContextMenuTrigger asChild>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent ref={contentRef} className="w-52">
        {/* Open in new tab */}
        <ContextMenuItem
          onClick={() => window.open(`/auction/${auctionId}`, "_blank")}
          className="gap-2"
        >
          <ExternalLink size={14} />
          In neuem Tab öffnen
        </ContextMenuItem>

        {/* Watchlist toggle */}
        {canWatchlist && (
          <ContextMenuItem
            onClick={() => toggle(rawAuctionKey, isInWatchlist)}
            disabled={isPending}
            className="gap-2"
          >
            {isInWatchlist ? <EyeOff size={14} /> : <Eye size={14} />}
            {isInWatchlist ? "Von Watchlist entfernen" : "Zur Watchlist hinzufügen"}
          </ContextMenuItem>
        )}

        <ContextMenuSeparator />

        {/* Seller profile */}
        <ContextMenuItem
          onClick={() => navigate(`/player/${auction.seller}`)}
          className="gap-2"
        >
          <User size={14} />
          Verkäufer anzeigen
        </ContextMenuItem>

        <ContextMenuSeparator />

        {/* Copy actions */}
        <ContextMenuItem
          onClick={() => copyToClipboard(auctionUrl, "Link")}
          className="gap-2"
        >
          <Copy size={14} />
          Link kopieren
        </ContextMenuItem>

        <ContextMenuItem
          onClick={() => copyToClipboard(auctionId, "Auktions-ID")}
          className="gap-2"
        >
          <Hash size={14} />
          Auktions-ID kopieren
        </ContextMenuItem>

        <ContextMenuItem
          onClick={() => copyToClipboard(priceText, "Preis")}
          className="gap-2"
        >
          <Coins size={14} />
          Preis kopieren
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
});

AuctionCardContextMenu.displayName = "AuctionCardContextMenu";

export default AuctionCardContextMenu;
